// @ts-nocheck
import React, { useMemo, useState, useEffect } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Scatter, Line, Bar } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import zoomPlugin from "chartjs-plugin-zoom";
import { setAlertHighlightIndex } from "../../store/forecastSlice";
import useIsMobile from "../../hooks/useIsMobile";
import { dismissTooltipPlugin, verticalLineIntersect } from "./helpers/GraphPlugins";
import GraphDesktopControls from "./helpers/GraphDesktopControls";
import GraphMobileTimeLabel from "./helpers/GraphMobileTimeLabel";
import GraphModals from "./helpers/GraphModals";
import useGraphControls from "./helpers/useGraphControls";

import {
  applyMobileTicks,
  buildXAxisTicks,
  buildZoomOptions,
  createMonthDayTickCallback,
  createTickCallback,
  TIME_RANGES,
} from "./graphutil";
import { buildTooltipOptions, formatDateTimeLabel } from "./forecastUtil";

ChartJS.register(...registerables, annotationPlugin, zoomPlugin);

const percentileColors = [
  "#cfd5b3",
  "#abb57b",
  "#5e692e",
  "#abb57b",
  "#cfd5b3",
];

const areNumbersSwitched = (str1, str2) => {
  // Extract the first 5 characters and split by "-"
  const [num1a, num1b] = str1.substring(0, 5).split("-");
  const [num2a, num2b] = str2.substring(0, 5).split("-");

  // Check if the numbers are switched
  return num1a === num2b && num1b === num2a;
};

const ScatterGraph = React.memo(
  ({
    data,
    labels,
    highResData,
    control,
    average,
    graphCastData,
    alerts,
    yaxis,
    timeZone,
    lineTension = 0.2,
    title = "Weather Forecast",
    onTimeZoneChange,
    onRegisterControls,
    desktopExtraControls,
    hideGraphControls = false,
  }) => {
    const dispatch = useDispatch();
    const forecast = useSelector((state) => state.forecast);
    const isMobile = useIsMobile();
    const chartRef = React.useRef(null);
    const currentViewRef = React.useRef({ 
      start: new Date(),
      end: new Date()
    });
    const [isDatasetModalOpen, setIsDatasetModalOpen] = useState(false);
    const [isTimeRangeModalOpen, setIsTimeRangeModalOpen] = useState(false);
    const [visibleDatasets, setVisibleDatasets] = useState({});
    const [selectedTimeRange, setSelectedTimeRange] = useState('full');
    const [isZoomed, setIsZoomed] = useState(false);

    useGraphControls({
      onRegisterControls,
      setIsTimeRangeModalOpen,
      setIsDatasetModalOpen,
      currentViewRef,
      selectedTimeRange,
    });

    const handleResetZoom = () => {
      if (chartRef.current) {
        chartRef.current.resetZoom();
        setIsZoomed(false);
      }
    };

    // Add double-click to reset zoom
    useEffect(() => {
      let canvas: HTMLCanvasElement | null = null;
      const handleDoubleClick = () => {
        handleResetZoom();
      };

      // Use a small delay to ensure chart is fully initialized
      const timer = setTimeout(() => {
        const chart = chartRef.current;
        if (!chart || !chart.canvas) return;
        canvas = chart.canvas;
        canvas.addEventListener('dblclick', handleDoubleClick);
      }, 100);

      return () => {
        clearTimeout(timer);
        if (canvas) {
          canvas.removeEventListener('dblclick', handleDoubleClick);
        }
      };
    }, []);

    // Craig Extract to a util
    const maxYValue = useMemo(
      () =>
        [...Object.values(data), highResData, control, average, graphCastData?.data ? [graphCastData.data] : []]
          .flat()
          .reduce((max, item) => Math.max(max, item?.y), 0) * 1.1,
      [data, highResData, control, average, graphCastData]
    );

    const maxXValue = [...Object.values(data), highResData, control, average, graphCastData?.data ? [graphCastData.data] : []]
      .flat()
      .reduce((max, item) => Math.max(max, new Date(item?.x).getTime()), 0);

    const minXValue = [...Object.values(data), highResData, control, average, graphCastData?.data ? [graphCastData.data] : []]
      .flat()
      .reduce(
        (min, item) => Math.min(min, new Date(item?.x).getTime()),
        Infinity
      );

    const handleToggleVisibility = (e, legendItem) => {
      const index = legendItem.datasetIndex;
      const ci = e.chart;
      const meta = ci.getDatasetMeta(index);

      // Toggle the visibility
      meta.hidden = !meta.hidden;

      // Also toggle other datasets with the same group label
      ci.data.datasets.forEach((dataset, i) => {
        if (areNumbersSwitched(dataset.label, legendItem.text)) {
          ci.getDatasetMeta(i).hidden = meta.hidden;
        }
      });

      ci.update();
    };

    // Calculate time window based on the current time and data
    const getTimeWindow = (hoursToShow) => {
      if (!data || Object.values(data).length === 0) {
        return { start: new Date(), end: new Date() };
      }
      
      // Get all time points from the data
      const allTimePoints = [...Object.values(data)]
        .flat()
        .map(item => new Date(item.x).getTime())
        .sort((a, b) => a - b);
      
      // Find the closest point to current time
      const nowTime = new Date().getTime();
      let closestPointIndex = 0;
      let minDiff = Infinity;
      
      allTimePoints.forEach((time, index) => {
        const diff = Math.abs(time - nowTime);
        if (diff < minDiff) {
          minDiff = diff;
          closestPointIndex = index;
        }
      });
      
      // If we have a full range request, just return min/max of all data
      if (hoursToShow === 'full') {
        return {
          start: new Date(minXValue),
          end: new Date(maxXValue)
        };
      }
      
      // Otherwise, use the hours parameter to define the window
      const startTime = allTimePoints[Math.max(0, closestPointIndex)];
      const endTime = startTime + (parseInt(hoursToShow) * 60 * 60 * 1000);
      
      return {
        start: new Date(startTime),
        end: new Date(endTime)
      };
    };

    // Memoize the initial time window (default to full range on all devices)
    const initialTimeWindow = useMemo(() => getTimeWindow('full'), [data]);

    // Apply time range to chart
    const applyTimeRange = (rangeValue) => {
      if (!chartRef.current) return;
      
      const timeWindow = getTimeWindow(rangeValue);
      
      chartRef.current.zoomScale('x', {
        min: timeWindow.start.getTime(),
        max: timeWindow.end.getTime()
      });
      
      // Update the current view reference
      currentViewRef.current = timeWindow;
      
      // Update the selected time range
      setSelectedTimeRange(rangeValue);
    };

    const options = useMemo(
      () => ({
        tension: lineTension,
        interaction: {
          mode: "nearest",
          intersect: false,
          axis: "x",
        },
        plugins: {
          zoom: {
            ...buildZoomOptions({ 
              isMobile, 
              minXValue, 
              maxXValue, 
              minYValue: 0, 
              maxYValue, 
              currentViewRef,
              onZoomReset: () => setIsZoomed(false)
            }),
            onZoomComplete: () => setIsZoomed(true),
            onZoomReset: () => setIsZoomed(false),
          },
          tooltip: buildTooltipOptions({
            timeZone,
            position: "nearest",
            labelFormatter: function (context) {
              const label = context.dataset.label || "";
              const value = context.parsed.y.toFixed(1);

              const regex = /^(\d+)-\d+ Percentile$/;
              const match = label.match(regex);

              if (match) {
                return `${match[1]} Percentile: ${value}`;
              }

              return `${label}: ${value}`;
            },
          }),

          verticalLine: {},
          annotation: {
            annotations: {
              currentUTCTime: {
                type: "line",
                xMin: new Date().toISOString(),
                xMax: new Date().toISOString(),
                borderColor: "rgb(255, 99, 132)",
                borderWidth: 2,
                label: {
                  display: !isMobile,
                  content: () => formatDateTimeLabel(new Date(), timeZone),
                  enabled: true,
                  position: "end",
                },
              },
            },
          },
          legend: {
            display: false, // Hide legend on all screens
            position: 'top',
            onClick: handleToggleVisibility,
            labels: {
              filter: function (legendItem, chartData) {
                // Hide 'Dataset 2' from the legend
                const firstFive = legendItem.text.substring(0, 5);
                const percentileFlag = firstFive.match(/(\d+)-(\d+)/);
                if (!percentileFlag) {
                  return true;
                }
                const [first, second] = firstFive.split("-");

                return second >= first;
              },
            },
          },
        },
        maintainAspectRatio: false,
        spanGaps: true,
        scales: {
          x: {
            stacked: true,
            display: true,
            type: "time",
            // Apply time range based on device type
            min: initialTimeWindow.start.getTime(),
            max: initialTimeWindow.end.getTime(),
            offset: false,
            grid: {
              ...(isMobile
                ? { drawBorder: true, drawTicks: false, borderWidth: 2, borderColor: "#111827" }
                : {}),
            },
            title: {
              display: true,
              text: "",
            },
            ticks: buildXAxisTicks(
              isMobile ? createMonthDayTickCallback() : createTickCallback(),
              isMobile
            ),
          },

          y: {
            display: true,
            title: {
              display: !isMobile,
              text: yaxis,
            },
            max: Math.ceil(maxYValue),
            grid: {
              ...(isMobile
                ? { drawBorder: false } // MIKE ADD remove left border line on graph for mobile
                : {}),
            },
            border: isMobile ? { display: false } : undefined, // MIKE ADD remove left border line on graph for mobile
            ticks: applyMobileTicks({ display: true }, isMobile, {
              mirror: false,
              padding: 4,
              font: { size: 8 },
            }),
          },
        },
        layout: { // MIKE ADD. shift graph to right on mobile to make space for y axis labels. can be edited
          padding: {
            left: isMobile ? 1 : 0,
          },
        },
      }),
      [maxYValue, lineTension, timeZone, isMobile, initialTimeWindow, minXValue, maxXValue]
    );

    const boxplotData = useMemo(
      () => ({
        // define label tree
        // labels: labels,
        datasets: [
          {
            label: `Control`,
            type: "line",
            radius: 0,
            backgroundColor: "rgba(66,165,245, 0.5)",
            borderColor: "rgba(66,165,245, 0.5)",
            data: control,
            // backgroundColor: 'rgba(255, 255, 255, 1)', // Customize as needed
            // borderColor: `rgba(255, 255, 255, 1)`, // Customize as needed
            // borderWidth: 1,
          },
          {
            label: `Average`,
            type: "line",
            radius: 0,
            backgroundColor: "rgba(239,83,80, 0.5)",
            borderColor: "rgba(239,83,80, 0.5)",
            data: average,
          },
          {
            label: "Hourly",
            type: "line",
            data: highResData,
            backgroundColor: "rgba(75, 0, 130, 0.5)",
            borderColor: "rgba(75, 0, 130, 0.5)",
            radius: 0,
          },
          ...(graphCastData ? [{
            label: "GraphCast",
            type: "line",
            data: graphCastData.data,
            backgroundColor: graphCastData.color || "#8a2be2",
            borderColor: graphCastData.color || "#8a2be2",
            radius: 0,
            borderWidth: 2,
          }] : []),
          // {
          //   label: `calculated average`,
          //   type: "line",
          //   radius: 1,
          //   backgroundColor: "rgba(255,140,0,0.5)",
          //   borderColor: "rgba(255,140,0,0.5)",
          //   data: controlAverage[2],
          // },
          ...Object.values(data).map((arr, index) => {
            const step = 20;
            const percentile = 10 + index * step;
            const oppositePercentile = 100 - percentile;
            let label = `${percentile}-${oppositePercentile} Percentile`;
            if (percentile === oppositePercentile) {
              label = `${percentile} Percentile`;
            }
            return {
              label,
              type: "line",
              data: arr,
              radius: 0,
              backgroundColor: percentileColors[index], // Customize as needed
              borderColor: percentileColors[index], // Customize as needed
              borderWidth: 1,
              fill:
                Math.floor(arr.length / 2) === index
                  ? false
                  : index < 2
                  ? `+1`
                  : "-1", // Fill down to the next dataset
            };
          }),
        ],
      }),
      [highResData, data, control, average, graphCastData]
    );

    // Apply initial time range on component mount
    useEffect(() => {
      if (chartRef.current && data) {
        // Set the initial time window
        const timeWindow = getTimeWindow(selectedTimeRange);
        
        chartRef.current.zoomScale('x', {
          min: timeWindow.start.getTime(),
          max: timeWindow.end.getTime()
        });
        
        // Store the initial view reference
        currentViewRef.current = timeWindow;
      }
    }, [data, selectedTimeRange]);
    
    // Initialize visibleDatasets when boxplotData changes
    useEffect(() => {
      if (boxplotData && boxplotData.datasets) {
        const initialVisibility = {};
        boxplotData.datasets.forEach((dataset, index) => {
          initialVisibility[index] = true;
        });
        setVisibleDatasets(initialVisibility);
      }
    }, [boxplotData]);
    
    if (!data) {
      return false;
    }
    
    // Toggle dataset visibility
    const toggleDatasetVisibility = (index) => {
      if (chartRef.current) {
        const chart = chartRef.current;
        const meta = chart.getDatasetMeta(index);
        
        // Toggle the visibility
        meta.hidden = !meta.hidden;
        
        // Also toggle other datasets with the same group label
        boxplotData.datasets.forEach((dataset, i) => {
          if (dataset.label && boxplotData.datasets[index].label && 
              areNumbersSwitched(dataset.label, boxplotData.datasets[index].label)) {
            chart.getDatasetMeta(i).hidden = meta.hidden;
            setVisibleDatasets(prev => ({...prev, [i]: !meta.hidden}));
          }
        });
        
        setVisibleDatasets(prev => ({...prev, [index]: !meta.hidden}));
        chart.update();
      }
    };
    
    // Prepare datasets for the modal
    const datasetItems = boxplotData.datasets.map((dataset, index) => {
      // Skip percentile datasets with reversed numbers
      const firstFive = dataset.label?.substring(0, 5) || '';
      const percentileFlag = firstFive.match(/(\d+)-(\d+)/);
      if (percentileFlag) {
        const [first, second] = firstFive.split("-");
        if (Number(second) < Number(first)) return null;
      }
        
      return {
        id: index.toString(),
        label: dataset.label,
        visible: visibleDatasets[index] !== false,
        color: dataset.borderColor || dataset.backgroundColor
      };
    }).filter(Boolean);

    if (data) {
      return (
        <div className="flex flex-col w-full h-full">
          {!hideGraphControls && (
            <GraphDesktopControls
              selectedTimeRange={selectedTimeRange}
              timeRanges={TIME_RANGES}
              onOpenTimeRange={() => setIsTimeRangeModalOpen(true)}
              onOpenDataset={() => setIsDatasetModalOpen(true)}
              onResetZoom={handleResetZoom}
              isZoomed={isZoomed}
            />
          )}
          
          <div className="flex-1 min-h-0 relative">
          {isMobile && (
            <div className="absolute left-[3px] top-[29px] bottom-[22px] w-0.5" /> // MIKE ADD. added left 3px to shift line right on mobile and a black line
          )}
            <GraphMobileTimeLabel isMobile={isMobile} timeZone={timeZone} />
            <Line
              ref={chartRef}
              className="h-full w-full"
              options={options}
              data={boxplotData}
              plugins={[verticalLineIntersect, dismissTooltipPlugin]}
            />
          </div>

          <GraphModals
            isDatasetOpen={isDatasetModalOpen}
            isTimeRangeOpen={isTimeRangeModalOpen}
            onCloseDataset={() => setIsDatasetModalOpen(false)}
            onCloseTimeRange={() => setIsTimeRangeModalOpen(false)}
            datasets={datasetItems}
            onToggleVisibility={(id) => toggleDatasetVisibility(parseInt(id))}
            timeRanges={TIME_RANGES}
            selectedTimeRange={selectedTimeRange}
            onSelectTimeRange={applyTimeRange}
            timeZone={timeZone}
            onTimeZoneSelect={
              typeof onTimeZoneChange === 'function' ? (zone) => onTimeZoneChange(zone) : undefined
            }
          />
        </div>
      );
    }
    return false;
  }
);

export default ScatterGraph;
